// Phone follow-up panel for request-specific no-email patient contact tasks.

import type { NavigatorPatientContactTask } from "../types";

type PatientPhoneFollowUpPanelProps = {
  tasks: NavigatorPatientContactTask[];
  phoneNumber: string | null | undefined;
  recordingProviderRequestId: number | null;
  error: string | null;
  onMarkInformed: (task: NavigatorPatientContactTask) => void;
  onViewProviderProfile?: (familyDoctorId: number) => void;
  onFindAnotherProvider?: (() => void) | null;
  canFindAnotherProvider?: boolean;
};

function getTaskInstruction(task: NavigatorPatientContactTask): string {
  if (task.contactType === "MeetAndGreet") {
    return "Contact the patient to arrange the Meet & Greet.";
  }
  return "Contact the patient to explain the waitlist status.";
}

function getTaskReasonLabel(task: NavigatorPatientContactTask): string {
  if (task.contactType === "MeetAndGreet") {
    return "Meet & Greet — call patient";
  }
  return "Waitlist — call patient";
}

export default function PatientPhoneFollowUpPanel({
  tasks,
  phoneNumber,
  recordingProviderRequestId,
  error,
  onMarkInformed,
  onViewProviderProfile,
  onFindAnotherProvider,
  canFindAnotherProvider = false,
}: PatientPhoneFollowUpPanelProps) {
  if (!Array.isArray(tasks) || tasks.length === 0) {
    return null;
  }

  const displayPhone = String(phoneNumber || "").trim() || "—";
  const showFindAnother =
    canFindAnotherProvider &&
    typeof onFindAnotherProvider === "function" &&
    tasks.some((task) => task.contactType === "Waitlist");

  return (
    <section
      className="cn-case-detail-card cn-case-detail-wide cn-phone-follow-up"
      aria-label="Patient phone follow-up required"
    >
      <div className="cn-case-detail-card-header">
        <h2>Patient phone follow-up required</h2>
      </div>

      {showFindAnother ? (
        <p className="cn-phone-follow-up-rematch-note">
          The patient can remain on this waitlist while you look for another
          provider.
        </p>
      ) : null}

      <dl className="cn-case-detail-list">
        <div className="cn-case-detail-item cn-phone-follow-up-phone">
          <dt>Patient phone</dt>
          <dd>{displayPhone}</dd>
        </div>
      </dl>

      {error ? (
        <p className="cn-phone-follow-up-error" role="alert">
          {error}
        </p>
      ) : null}

      <ul className="cn-phone-follow-up-tasks">
        {tasks.map((task) => {
          const doctorId = Number(task.familyDoctorProfileId || 0);
          const canView =
            typeof onViewProviderProfile === "function" &&
            Number.isFinite(doctorId) &&
            doctorId > 0;
          const isRecording =
            recordingProviderRequestId !== null &&
            Number(recordingProviderRequestId) ===
              Number(task.providerRequestId);

          return (
            <li
              key={task.providerRequestId}
              className="cn-phone-follow-up-task"
            >
              <p className="cn-phone-follow-up-instruction">
                {getTaskInstruction(task)}
              </p>
              <dl className="cn-case-detail-list cn-case-detail-list-three">
                <div className="cn-case-detail-item">
                  <dt>Provider</dt>
                  <dd>{task.doctorName || "—"}</dd>
                </div>
                <div className="cn-case-detail-item">
                  <dt>Clinic</dt>
                  <dd>{task.clinicName || "—"}</dd>
                </div>
                <div className="cn-case-detail-item">
                  <dt>Reason</dt>
                  <dd>{getTaskReasonLabel(task)}</dd>
                </div>
              </dl>
              <div className="cn-follow-up-outcome-action">
                {canView ? (
                  <button
                    type="button"
                    className="cn-follow-up-outcome-secondary"
                    onClick={() => onViewProviderProfile?.(doctorId)}
                  >
                    View provider profile
                  </button>
                ) : null}
                {task.contactType === "Waitlist" && showFindAnother ? (
                  <button
                    type="button"
                    className="cn-follow-up-outcome-secondary"
                    onClick={() => onFindAnotherProvider?.()}
                  >
                    Find another provider
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={isRecording}
                  onClick={() => onMarkInformed(task)}
                >
                  {isRecording ? "Saving…" : "Mark patient informed"}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
